/**
 * Shared front-end phone normaliser (imported by every browser bundle that
 * collects a phone number: the customer portal, the public booking/waitlist SPA,
 * and the admin SPA). Keeping a single source here means the E.164 promotion
 * rules cannot drift between bundles.
 *
 * Normalise a free-text phone entry to E.164 (`/^\+[1-9]\d{1,14}$/`). The market
 * is Ireland (see CLAUDE.md), so a bare national number with a trunk `0` is
 * promoted to +353. Returns the E.164 string, or `null` when the input cannot be
 * made valid — the caller surfaces a clear inline message instead of the opaque
 * server-side 422 that a raw local number (e.g. "0871234567") would trigger.
 */
export function normalizeE164(raw: string, defaultCountryCode = '353'): string | null {
  // Keep a leading '+' but strip spaces, dashes, dots, and (parentheses).
  const cleaned = raw.trim().replace(/[\s().-]/g, '');
  if (cleaned === '') return null;

  let candidate: string;
  if (cleaned.startsWith('+')) {
    candidate = cleaned;
  } else if (cleaned.startsWith('00')) {
    // International access code → '+'.
    candidate = `+${cleaned.slice(2)}`;
  } else if (cleaned.startsWith('0')) {
    // National trunk prefix → default country code (drop the single leading 0).
    candidate = `+${defaultCountryCode}${cleaned.slice(1)}`;
  } else if (cleaned.startsWith(defaultCountryCode)) {
    // Already country-code-prefixed but missing the '+'.
    candidate = `+${cleaned}`;
  } else if (/^\d+$/.test(cleaned)) {
    // Bare local digits with no trunk 0 → assume the default country.
    candidate = `+${defaultCountryCode}${cleaned}`;
  } else {
    return null;
  }

  return /^\+[1-9]\d{1,14}$/.test(candidate) ? candidate : null;
}
